import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icons } from "../components/ui/icons";
import SearchInput from "../components/search-input";
import DataTable, { TableRowData } from "../components/data-table";
import { quotesTableColumns } from "../lib/table-columns";
import { Button } from "../components/ui/button";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import BarGraph from "../components/bar-graph";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import QuoteDrawer from "../components/quote-drawer";
import { useNavigate } from "react-router-dom";
import { getQuotations, getQuotationStatistics, Quotation, QuotationStatistics, updateQuotationStatus } from "../lib/api";
import { Edit2 } from "lucide-react";
import { Modal } from "../components/ui/modal";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";

const Quotes: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedQuoteDetails, setSelectedQuoteDetails] =
    useState<TableRowData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingQuote, setRejectingQuote] = useState<TableRowData | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);

  // Approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState<TableRowData | null>(null);
  const [isApproving, setIsApproving] = useState(false);

  const queryClient = useQueryClient();
  const statusParam = currentTab === "All" ? undefined : currentTab;

  const quotationsQuery = useQuery({
    queryKey: ["quotations", { page: currentPage, limit: pageSize, search: searchTerm, status: statusParam }],
    queryFn: async () => getQuotations(currentPage, pageSize, searchTerm, statusParam),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<QuotationStatistics>({
    queryKey: ["quotations", "statistics"],
    queryFn: async () => getQuotationStatistics(),
  });

  useEffect(() => {
    if (!quotationsQuery.error) return;
    toast.error(getBackendErrorMessage(quotationsQuery.error, "Failed to fetch quotations data"));
  }, [quotationsQuery.error]);

  useEffect(() => {
    if (!statisticsQuery.error) return;
    toast.error(getBackendErrorMessage(statisticsQuery.error, "Failed to fetch quotation statistics"));
  }, [statisticsQuery.error]);

  const quotations = quotationsQuery.data?.data ?? [];
  const statistics = statisticsQuery.data ?? null;
  const totalItems = quotationsQuery.data?.meta.total ?? 0;
  const totalPages = quotationsQuery.data?.meta.totalPages ?? 1;
  const initialLoading = quotationsQuery.isLoading;
  const tableLoading = quotationsQuery.isFetching && !quotationsQuery.isLoading;

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => updateQuotationStatus(id, status as any),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["quotations"] }),
        queryClient.invalidateQueries({ queryKey: ["quotations", "statistics"] }),
      ]);
    },
  });

  const handleConfirmReject = async () => {
    if (!rejectingQuote) return;
    setIsRejecting(true);
    try {
      await updateStatusMutation.mutateAsync({ id: rejectingQuote.id as string, status: "REJECTED" });
      setShowRejectModal(false);
      setRejectingQuote(null);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to reject quotation"));
    } finally {
      setIsRejecting(false);
    }
  };

  // Reset pagination on tab changes (search handled separately).
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), reset pagination.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  const tableRows = useMemo(
    () =>
      quotations.map((quotation) => ({
        id: quotation.id,
        reference: quotation.reference,
        customer: quotation.customer?.fullName ?? "—",
        site: quotation?.site?.name ?? "",
        amount: `${quotation.currencyType} ${parseFloat(quotation.totalAmount || "0").toLocaleString()}`,
        status: quotation.status,
        issueDate: new Date(quotation.issueDate).toLocaleDateString(),
        expiryDate: new Date(quotation.expiryDate).toLocaleDateString(),
      })),
    [quotations],
  );
  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedQuoteDetails(row);
      },
    },
    {
      label: "Copy to new quotation",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", { state: { duplicateQuotationId: row.id } });
      },
    },
    {
      label: "Copy to new Invoice",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { quotationId: row.id } });
      },
    },
    {
      label: "Edit",
      icon: Edit2,
      visible: (row: TableRowData) => String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", { state: { quotationId: row.id } });
      },
    },
    {
      label: "Approve",
      icon: Icons.Check,
      visible: (row: TableRowData) => String(row.status).toUpperCase() !== "APPROVED" && String(row.status).toUpperCase() !== "REJECTED",
      onClick: (row: TableRowData) => {
        setApprovingQuote(row);
        setShowApproveModal(true);
      },
    },
    {
      label: "Reject",
      icon: Icons.Close,
      visible: (row: TableRowData) => String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        setRejectingQuote(row);
        setShowRejectModal(true);
      },
    },
  ];

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["quotations"] }),
      queryClient.invalidateQueries({ queryKey: ["quotations", "statistics"] }),
    ]);

  const openNewQuote = () => {
    navigate("/create-quotation");
  };

  return (
    <>
      <QuoteDrawer
        selectedQuoteDetails={selectedQuoteDetails}
        quoteDetails={selectedQuoteDetails}
        onClose={() => {
          setSelectedQuoteDetails(null);
        }}
      />
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Confirm Reject"
        description={rejectingQuote ? `Are you sure you want to reject quotation ${rejectingQuote.reference || rejectingQuote.id}? This action will mark the quotation as REJECTED.` : "Are you sure you want to reject this quotation?"}
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button className="bg-red-600 text-white" onClick={handleConfirmReject} disabled={isRejecting}>
              {isRejecting ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Confirm Approve"
        description={approvingQuote ? `Approve quotation ${approvingQuote.reference || approvingQuote.id}? This will mark it as APPROVED and open the Create Invoice page prefilled with the quotation.` : "Are you sure you want to approve this quotation?"}
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary-600 text-white"
              onClick={async () => {
                if (!approvingQuote) return;
                setIsApproving(true);
                try {
                  await updateStatusMutation.mutateAsync({ id: approvingQuote.id as string, status: "APPROVED" });
                  setShowApproveModal(false);
                  const q = approvingQuote;
                  setApprovingQuote(null);
                  // Navigate to create invoice page prefilled from quotation
                  navigate("/create-invoice", { state: { quotationId: q.id } });
                } catch (error) {
                  toast.error(getBackendErrorMessage(error, "Failed to approve quotation"));
                } finally {
                  setIsApproving(false);
                }
              }}
              disabled={isApproving}
            >
              {isApproving ? "Approving..." : "Confirm Approve"}
            </Button>
          </div>
        </div>
      </Modal>
      <PageShell
        title="Quotes"
        description="Quickly create, send, and manage equipment rental quotes for your clients."
        loading={initialLoading}
        toolbar={
          <div className="flex items-center gap-3">
            <RefreshToolbarButton
              isRefreshing={quotationsQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewQuote}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Quote
            </Button>
          </div>
        }
        stats={[
          {
            label: "Total",
            value: statistics?.totalQuotations?.toString() || "0",
            icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Approved",
            value: statistics?.byStatus?.find(s => s.status === "APPROVED")?.count?.toString() || "0",
            icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Pending",
            value: statistics?.byStatus?.find(s => s.status === "PENDING")?.count?.toString() || "0",
            icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4">
            <HorizontalBarGraph
              graphTitle="Quotes by Clients"
              data={statistics?.topCustomers?.slice(0, 3).map(customer => ({
                category: customer.customerName,
                gap: 0.2,
                values: [
                  { label: "Total", value: customer.quotationCount, fillColor: "#bce3ff" },
                ],
              })) || []}
            />
            <BarGraph
              graphTitle="Quotes by Status"
              metrics={(statistics?.byStatus?.map(status => ({
                label: status.status,
                count: status.count,
                percentage: Math.round((status.count / (statistics?.totalQuotations || 1)) * 100),
                avgDays: 0,
              })) || []).sort((a, b) => b.count - a.count)}
            />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Quote", icon: Icons.Plus, onClick: openNewQuote },
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabBar>
            {[
              { label: "All", count: statistics?.totalQuotations || 0 },
              ...(statistics?.byStatus?.map((s) => ({ label: s.status, count: s.count })) || []),
            ].map((tab) => (
              <TabButton
                key={tab.label}
                label={tab.label}
                count={tab.count}
                active={currentTab === tab.label}
                onClick={() => {
                  setCurrentTab(tab.label);
                }}
              />
            ))}
          </TabBar>
        </div>
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search items..."
        />
        <DataTable
          columns={quotesTableColumns}
          data={tableRows}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Quotes;
